import { Form, Input, Tree } from 'antd'
import { CarOutlined } from '@ant-design/icons'
import styles from './index.module.scss'
import LoadingWrapper from '../loadingWrapper';
import {
  ExtendedDataNode,
  handleCateData,
  useCarTreeUtils,
  CarOnlineStatus,
  generateCarKey,
  generateCateKey,
  parseIdFromKey
} from './useCarTreeUtils';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { groupCategoryRequestV2 } from '@/service/groupManage';
import { filterMultipleCateData } from '@/utils/cate';
import { carTreeRequestV3, getCateCarInfoRequestV2 } from '@/service/carTree';

// 定义组件实例方法接口
export interface CarTreeV2Ref {
  getFilteredSelectedNodes: () => Promise<React.Key[]>;
  getFilteredSelectedNodesData: () => Promise<any[]>;
}
interface CarTreeV2Props {
  checkable?: boolean; // 是否显示复选框
  searchAlive?: boolean; // 搜索之后的勾选是否包含搜索前的数据
  onFilteredSelectionChange?: (filteredKeys: React.Key[]) => void; // 过滤后的选中节点变化回调
}

const CarTreeV2 = forwardRef<CarTreeV2Ref, CarTreeV2Props>((props, ref) => {
  const { checkable = false, onFilteredSelectionChange } = props;
  const [form] = Form.useForm();
  const userStore = useSelector((state: any) => state.userStore);
  const { userDetail } = userStore;
  const cateTree = useSelector((state: any) => state.cateTreeStore.cateTree);
  const isMountedRef = useRef(true); //组件绑定标志位
  const timerRef = useRef<any | null>(null); // 定时器引用
  const cateCarTreeDataRef = useRef<ExtendedDataNode[]>([]); // 用ref存储最新的树数据
  const [isLoading, setIsLoading] = useState(false);

  //更新树上分组的展示
  //初始化的时候树上没有车，展开的时候会查询一次
  const [cateCarTreeData, setCateCarTreeData] = useState<ExtendedDataNode[]>([]); // 展示的树形车辆数据

  // 同步更新ref中的数据
  useEffect(() => {
    cateCarTreeDataRef.current = cateCarTreeData;
  }, [cateCarTreeData]);

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);// 存储所有展开的节点
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);// 存储所有选中的节点

  // 使用工具 hook
  const {
    filterSelectedNodes,
    findNodeByKey,
    getAllVisibleCarNodes,
    getAllCarNodes,
    isCarOnLine,
    filterSelectedNodesWithDetails
  } = useCarTreeUtils(cateCarTreeData);

  // 根据车辆状态返回对应颜色的图标
  const getCarIcon = (carData: any) => {
    if (!carData) return <CarOutlined style={{ color: '#cccccc' }} />;

    const status = isCarOnLine(carData);
    switch (status) {
      case CarOnlineStatus.Online_AccOn:
        return <CarOutlined style={{ color: '#52c41a' }} />; // 绿色
      case CarOnlineStatus.Online_AccOff:
        return <CarOutlined style={{ color: '#faad14' }} />; // 黄色
      default:
        return <CarOutlined style={{ color: '#cccccc' }} />; // 灰色
    }
  };

  // 更新树中车辆节点的状态和图标
  const updateCarNodesStatus = (carInfoList: any[]) => {
    if (!carInfoList || carInfoList.length === 0) return;

    const updateTreeData = (nodes: ExtendedDataNode[]): ExtendedDataNode[] => {
      return nodes.map(node => {
        if (node.isCar && node.id) {
          // 查找对应的车辆信息
          const carInfo = carInfoList.find(car => car.metaId === node.id);
          if (carInfo) {
            return {
              ...node,
              icon: getCarIcon(carInfo),
              carData: carInfo // 保存车辆数据用于状态判断
            };
          }
        }

        // 如果有子节点，递归更新
        if (node.children && node.children.length > 0) {
          return {
            ...node,
            children: updateTreeData(node.children)
          };
        }

        return node;
      });
    };

    const updatedTreeData = updateTreeData(cateCarTreeData);
    setCateCarTreeData(updatedTreeData);
  };

  // 定时查询车辆状态
  const startCarStatusPolling = () => {
    // 清除之前的定时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // 创建新的定时器
    timerRef.current = setInterval(async () => {
      if (!isMountedRef.current) return;

      //遍历所有节点，筛选出所有车辆的节点
      const visibleCarNodes = getAllCarNodes(cateCarTreeDataRef.current);
      if (visibleCarNodes.length === 0) return;
      const carIds = visibleCarNodes.map(node => node.id).filter(id => id !== undefined) as number[];
      // // 立即查询一次新添加车辆的状态
      try {
        const statusRes = await getCateCarInfoRequestV2(carIds);
        const carInfoList = statusRes.data?.data || [];
        setTimeout(() => {
          updateCarNodesStatus(carInfoList);
        }, 100); // 小延迟确保状态更新完成
      } catch (statusErr) {
        console.error('查询车辆状态失败:', statusErr);
      }


    }, 25000); // 每25秒查询一次
  };

  const upDateCateCarTreeData = async () => {
    let data;
    //如果有分类树数据，不重新请求
    if (cateTree.cateTree?.length) {
      data = cateTree.cateTree
    } else {
      setIsLoading(true);
      try {
        const res = await groupCategoryRequestV2();
        data = res.data?.data || [];
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    if (!isMountedRef.current) return;

    let cateData;
    if (userDetail.multiCate) {
      cateData = filterMultipleCateData(data, JSON.parse(userDetail.multiCate));
    } else {
      cateData = data;
    }
    const displayCateData = handleCateData(cateData);
    setCateCarTreeData(displayCateData);

    startCarStatusPolling();
  }
  useEffect(() => {
    upDateCateCarTreeData();
    return () => {
      isMountedRef.current = false;
      // 清理定时器
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  //节点展开相关操作
  //展开节点的时候会查询当前节点下的一级子节点车辆（不会递归查询）

  const onExpand = async (expandedKeysParam: React.Key[]) => {
    // 更新展开的节点状态
    setExpandedKeys(expandedKeysParam);

    const lastKey = expandedKeysParam[expandedKeysParam.length - 1];
    const targetNode = findNodeByKey(lastKey);
    if (!targetNode || targetNode.isLeaf) {
      return;
    }
    if (targetNode.hasSearch) {
      return;
    }
    let cars;
    try {
      const res = await carTreeRequestV3({ cateId: targetNode!.id as number });
      const resData = res.data?.data || [];
      cars = resData.map((item: any) => {
        return {
          id: item.metaId,
          title: item.title,
          key: generateCarKey(item.metaId), // 使用 car 前缀 + id
          isCar: true,
          isLeaf: true, // 车辆节点是叶子节点
          icon: getCarIcon(item), // 使用状态图标
          carData: item // 保存车辆数据
        } as ExtendedDataNode;
      })
      targetNode.hasSearch = true;
      targetNode.children = [...(targetNode.children || []), ...cars];

      // 更新状态以触发视图重新渲染
      if (cars.length) {
        setCateCarTreeData([...cateCarTreeData]);

        // // 立即查询一次新添加车辆的状态
        try {
          const statusRes = await getCateCarInfoRequestV2(cars.map((item: any) => item.id));
          const carInfoList = statusRes.data?.data || [];
          setTimeout(() => {
            updateCarNodesStatus(carInfoList);
          }, 100); // 小延迟确保状态更新完成
        } catch (statusErr) {
          console.error('查询车辆状态失败:', statusErr);
        }
      }


    } catch (err) {
      console.error(err);
    }
  };

  // 处理选中节点变化
  const onCheck = (checkedKeysParam: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {

    const keys = Array.isArray(checkedKeysParam) ? checkedKeysParam : checkedKeysParam.checked;
    setCheckedKeys(keys);

    // 使用过滤方法获取优化后的选中节点列表
    // const filteredKeys = filterSelectedNodes(keys);

    // 如果有回调函数，调用它传递过滤后的结果
    // if (onFilteredSelectionChange) {
    // onFilteredSelectionChange(filteredKeys);
    // }
  };



  useImperativeHandle(ref, () => ({
    //获取过滤后的选中节点
    getFilteredSelectedNodes: () => filterSelectedNodes(checkedKeys),
    //获取过滤后的选中节点数据
    getFilteredSelectedNodesData: () => filterSelectedNodesWithDetails(checkedKeys),
  }));

  return (
    <div className={styles.carTree}>
      {/* 组件标题 */}
      <div className={styles.title}>
        <div>
          车辆列表
        </div>
      </div>

      {/* 组件文本筛选 */}
      <Form form={form} autoComplete="off">
        <Form.Item name="cateName" noStyle>
          <Input placeholder='可以输入分组名进行筛选' />
        </Form.Item>
      </Form>

      <LoadingWrapper spinning={cateCarTreeData.length === 0 || isLoading} autoHeight>
        <div className={styles.tree}>
          <Tree
            checkable={checkable}
            treeData={cateCarTreeData}
            showLine={{ showLeafIcon: false }}
            showIcon
            expandedKeys={expandedKeys}
            checkedKeys={checkedKeys}
            onExpand={onExpand}
            onCheck={onCheck}
          />
        </div>
      </LoadingWrapper>
    </div>
  )
});

// 使用 useImperativeHandle 暴露方法给父组件
CarTreeV2.displayName = 'CarTreeV2';

export default CarTreeV2;